import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    Avatar,
    Button,
    Card,
    Drawer,
    Form,
    Input,
    InputNumber,
    message,
    Popconfirm,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { TablePaginationConfig } from 'antd/es/table'
import { UserOutlined } from '@ant-design/icons'
import { useSearchParams } from 'react-router-dom'
import { useAppContext } from '@/components/AppContext'
import { TrusteeCreatePayload } from '@a4b/api/src/modules/Monetisation/temple/types'
import S3Upload, { AllowedFormats } from '@a4b/ui/src/modules/monetisation/molecules/S3Upload'
import { YODA_CDN_URL, YODA_PRESIGN_URL } from '@/utils/constants'
import styled from 'styled-components'

/** ---------------- types ---------------- */

type Row = {
    id: string
    name: string
    designation?: string
    priority?: number
    avatarUrl?: string
    is_deleted?: boolean
    created_at?: string
}

/** ---------------- styled (top-level to avoid re-creation each render) ---------------- */

const Container = styled(Card)`
  display: flex;
  flex-direction: column;

  .ant-card-body {
    flex-grow: 1;
  }
`

/** ---------------- helpers (pure + stable) ---------------- */

// choose a URL string from an AntD Upload fileList or a string[] we set during edit
const pickUrlFromFileList = (list?: any[]): string => {
    if (!Array.isArray(list) || list.length === 0) return ''
    const file = list.find((x) => x?.status === 'done') ?? list[list.length - 1]
    if (typeof file === 'string') return file
    return (
        file?.url ||
        file?.thumbUrl ||
        file?.response?.url ||
        file?.response?.data?.url ||
        file?.response?.Location ||
        file?.response?.data?.Location ||
        ''
    )
}

/** AntD Upload-style normalizer: form stores a plain fileList array */
const normFileList = (e: any) => {
    if (Array.isArray(e)) return e
    return e?.fileList ?? []
}

const getString = (v: any): string => (typeof v === 'string' ? v : '')
const isUploading = (list: any[]) => list?.some((f) => f?.status && f.status !== 'done')

/** turn any absolute CDN URL into a path-only string (no scheme/host) */
const toCdnPathOnly = (url: string): string => {
    if (!url) return ''
    // already a path
    if (url.startsWith('/')) return url.split('?')[0]

    // absolute URL → use URL API to read pathname
    if (/^https?:\/\//i.test(url)) {
        try {
            const u = new URL(url)
            return u.pathname // path only, no query/hash
        } catch {
            // fall through
        }
    }

    // strip origin via regex if URL API failed
    const stripped = url.replace(/^https?:\/\/[^/]+/i, '')
    return stripped.startsWith('/') ? stripped.split('?')[0] : `/${stripped.split('?')[0]}`
}

/** for preview: path → absolute CDN URL; absolute stays absolute */
const toAbsoluteCdnUrl = (maybePath: string): string => {
    if (!maybePath) return ''
    if (/^https?:\/\//i.test(maybePath)) return maybePath
    const base = (YODA_CDN_URL || '').replace(/\/+$/, '')
    const path = maybePath.startsWith('/') ? maybePath : `/${maybePath}`
    return `${base}${path}`
}

const { Title } = Typography

/** ---------------- component ---------------- */

const ManageTrustees: React.FC = () => {
    const { networkInstance, countryState, languageState } = useAppContext()
    const [form] = Form.useForm()

    /** ---------- table (server-side) ---------- */
    const [pageRows, setPageRows] = useState<Row[]>([])
    const [total, setTotal] = useState<number>(0)
    const [loading, setLoading] = useState(false)

    const [pagination, setPagination] = useState<TablePaginationConfig>({
        current: 1,
        pageSize: 10,
    })

    const [nameFilter, setNameFilter] = useState('')
    const [idFilter, setIdFilter] = useState('')

    const [searchParams] = useSearchParams()
    // Extract stable primitives from search params (avoid depending on the object itself)
    const searchId = searchParams.get('id') ?? ''
    const searchName = searchParams.get('name') ?? ''
    const searchKey = `${searchId}|${searchName}`

    /** ---------- drawer / form ---------- */
    const [panelOpen, setPanelOpen] = useState(false)
    const [editingId, setEditingId] = useState<string | null>(null)

    // local preview / guard for uploader
    const [imageList, setImageList] = useState<any[]>([])

    /** ---------- mounted guard ---------- */
    const mountedRef = useRef(true)
    useEffect(() => {
        mountedRef.current = true
        return () => {
            mountedRef.current = false
        }
    }, [])

    /** ---------- mappers ---------- */
    const mapToRow = (t: any, keyForPage: number): Row => {
        const stableId = t?.id || t?.uuid || `tmp:${keyForPage}`
        return {
            id: stableId,
            name: t?.name || '',
            designation: t?.designation || '',
            priority: typeof t?.priority === 'number' ? t.priority : undefined,
            avatarUrl: t?.media?.url || t?.avatarUrl, // may be a path, we’ll fix for preview
            is_deleted: !!t?.is_deleted,
            created_at: t?.created_at,
        }
    }

    /** ---------- fetch one page from server (stable deps) ---------- */
    const getTrusteeList = useCallback(async () => {
        setLoading(true)
        try {
            const effectiveName = nameFilter.trim() || searchName || undefined
            const effectiveId = idFilter.trim() || searchId || undefined

            const pageSize = 10
            const current = Number(pagination.current || 1)
            const limit = pageSize
            const offset = (current - 1) * limit

            const res = await networkInstance.clientWithHeaders.shrineApi.getTrustees(
                { limit, offset },
                effectiveId,
                effectiveName,
                countryState?.countryCode,
                languageState?.monetisationLanguage
            )

            const api = (res?.data?.data ?? res?.data) as
                | { trustees?: any[]; count?: number }
                | undefined

            const list = Array.isArray(api?.trustees) ? api!.trustees : []
            const mapped = list.map((t, idx) => mapToRow(t, offset + idx))

            if (!mountedRef.current) return
            setPageRows(mapped)
            setTotal(typeof api?.count === 'number' ? api!.count : offset + mapped.length)
        } catch (e: any) {
            if (!mountedRef.current) return
            message.error(e?.response?.data?.message || 'Failed to fetch trustees')
        } finally {
            if (mountedRef.current) setLoading(false)
        }
    }, [
        pagination.current,
        nameFilter,
        idFilter,
        searchKey,
        countryState?.countryCode,
        languageState?.monetisationLanguage,
        networkInstance,
    ])

    useEffect(() => {
        getTrusteeList()
    }, [getTrusteeList])

    /** ---------- filters ---------- */
    const onNameFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.value
        setNameFilter(v)
        // reset to page 1 when filtering
        setPagination((prev) => (prev.current === 1 ? prev : { ...prev, current: 1 }))
    }

    const onIdFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.value
        setIdFilter(v)
        // reset to page 1 when filtering
        setPagination((prev) => (prev.current === 1 ? prev : { ...prev, current: 1 }))
    }

    const resetForm = () => {
        form.resetFields()
        setImageList([])
    }

    /** ---------- CRUD ---------- */
    const openCreate = () => {
        setEditingId(null)
        resetForm()
        setPanelOpen(true)
        form.setFieldsValue({ media: { type: '', url: [] } })
    }

    const openEdit = async (r: Row) => {
        setEditingId(r.id)
        setPanelOpen(true)
        setLoading(true)
        try {
            const { data } = await networkInstance.clientWithHeaders.shrineApi.getTrustee(r.id, countryState?.countryCode, languageState?.monetisationLanguage)
            const d = data?.data || data || {}

            const url = toAbsoluteCdnUrl(d?.media?.url || '')
            if (!mountedRef.current) return

            setImageList(url ? [url] : [])
            form.setFieldsValue({
                name: d.name || '',
                designation: d.designation || '',
                description: d.description || '',
                priority: typeof d.priority === 'number' ? d.priority : undefined,
                media: { type: getString(d?.media?.type ?? 'image'), url: url ? [url] : [] },
            })
        } catch (e: any) {
            if (mountedRef.current) {
                message.error(e?.response?.data?.message || 'Failed to fetch trustee')
            }
        } finally {
            if (mountedRef.current) setLoading(false)
        }
    }

    /** keep local imageList synced for preview/guards */
    const onValuesChange = (changed: any) => {
        if (changed?.media && 'url' in changed.media) {
            const list = changed.media.url
            if (Array.isArray(list)) setImageList(list)
        }
    }

    const onSave = async () => {
        try {
            await form.validateFields()
        } catch {
            return
        }

        // block if any file still uploading
        if (isUploading(imageList)) {
            message.warning('Please wait for the image upload to finish')
            return
        }

        // transform fileList -> URL string, then to CDN path only
        const fileList: any[] = form.getFieldValue(['media', 'url']) || []
        const fullUrl = pickUrlFromFileList(fileList) || ''
        const pathOnly = toCdnPathOnly(fullUrl)
        const mediaType = 'image'

        const values = form.getFieldsValue(true) as {
            name: string
            designation?: string
            description?: string
            priority?: number
        }

        const payload: TrusteeCreatePayload = {
            name: (values.name || '').trim(),
            designation: (values.designation || '').trim(),
            description: (values.description || '').trim(),
            media: { type: mediaType, url: pathOnly }, // <-- path only for backend
            priority: typeof values.priority === 'number' ? values.priority : undefined,
        }

        try {
            setLoading(true)
            if (editingId) {
                await networkInstance.clientWithHeaders.shrineApi.updateTrustee(editingId, payload, countryState?.countryCode, languageState?.monetisationLanguage)
                message.success('Trustee updated')
            } else {
                await networkInstance.clientWithHeaders.shrineApi.createTrustee(payload, countryState?.countryCode, languageState?.monetisationLanguage)
                message.success('Trustee created')
            }
            setPanelOpen(false)
            await getTrusteeList()
        } catch (e: any) {
            message.error(e?.response?.data?.message || 'Failed to save trustee')
        } finally {
            setLoading(false)
        }
    }

    const onDelete = async (id: string) => {
        try {
            setLoading(true)
            await networkInstance.clientWithHeaders.shrineApi.deleteTrustee(id, countryState?.countryCode, languageState?.monetisationLanguage)
            message.success('Trustee deleted')
            await getTrusteeList()
        } catch (e: any) {
            message.error(e?.response?.data?.message || 'Failed to delete trustee')
        } finally {
            setLoading(false)
        }
    }

    /** ---------- pagination ---------- */
    const onPageChange: NonNullable<TablePaginationConfig['onChange']> = (page) => {
        setPagination((prev) => ({ ...prev, current: page, pageSize: 10 }))
    }

    /** ---------- columns (avoid passing empty Avatar src) ---------- */
    const columns: ColumnsType<Row> = useMemo(
        () => [
            {
                title: (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span>Name</span>
                        {/* <Input
                            placeholder="Search Name"
                            value={nameFilter}
                            onChange={onNameFilterChange}
                            allowClear
                            size="small"
                        /> */}
                    </div>
                ),
                dataIndex: 'name',
                key: 'name',
                render: (_: any, r) => {
                    const src = r.avatarUrl ? toAbsoluteCdnUrl(r.avatarUrl) : undefined
                    return (
                        <Space>
                            <Avatar size={28} src={src} icon={!src && <UserOutlined />} />
                            <span style={{ fontWeight: 500 }}>{r.name}</span>
                        </Space>
                    )
                },
            },
            {
                title: (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span>Trustee Id</span>
                        <Input
                            placeholder="Search ID"
                            value={idFilter}
                            onChange={onIdFilterChange}
                            allowClear
                            size="small"
                        />
                    </div>
                ),
                dataIndex: 'id',
                key: 'id',
                width: '24%',
                ellipsis: true,
                render: (val: string) => (
                    <Tooltip placement="topLeft" title={val}>
                        {val}
                    </Tooltip>
                ),
            },
            { title: 'Designation', dataIndex: 'designation', key: 'designation', width: 220, ellipsis: true },
            { title: 'Priority', dataIndex: 'priority', key: 'priority', width: 110 },
            // {
            //     title: 'Status',
            //     key: 'status',
            //     width: 120,
            //     render: (_: any, r) => (r.is_deleted ? <Tag>Archived</Tag> : <Tag color="green">Active</Tag>),
            // },
            {
                title: 'Action',
                key: 'action',
                width: 240,
                render: (_: any, r) => {
                    return (
                        <Space size="middle">
                            <Button type="link" onClick={() => openEdit(r)}>
                                Edit
                            </Button>
                            <Popconfirm title="Delete trustee?" onConfirm={() => onDelete(r.id)}>
                                <Button type="link" danger>
                                    Delete
                                </Button>
                            </Popconfirm>
                        </Space>
                    )
                },
            },
        ],
        [nameFilter, idFilter, pageRows] // depend on filter inputs so titles update as you type
    )

    return (
        <Container
            title="Manage Trustees"
            style={{ width: '100%' }}
            extra={<Button type="primary" onClick={openCreate}>Add new Trustee</Button>}
        >
            {/* Server-side paginated grid */}
            <Table<Row>
                rowKey="id"
                loading={loading}
                columns={columns}
                dataSource={pageRows}
                pagination={{
                    current: pagination.current!,
                    pageSize: 10,
                    total,
                    onChange: onPageChange,
                    showSizeChanger: false,
                }}
            />

            <Drawer
                title={editingId ? 'Edit Trustee' : 'Create Trustee'}
                width={520}
                visible={panelOpen}
                onClose={() => setPanelOpen(false)}
                destroyOnClose
                footer={
                    <div style={{ textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => setPanelOpen(false)}>Cancel</Button>
                            <Button type="primary" onClick={onSave}>
                                {editingId ? 'Save Changes' : 'Create Trustee'}
                            </Button>
                        </Space>
                    </div>
                }
            >
                <Form
                    form={form}
                    layout="vertical"
                    name="trustee-form"
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                    onValuesChange={onValuesChange}
                    initialValues={{ media: { type: '', url: [] } }}
                >
                    <Form.Item
                        label="Name"
                        name="name"
                        rules={[{ required: true, message: 'Please input trustee name' }]}
                    >
                        <Input placeholder="Enter name" />
                    </Form.Item>

                    <Form.Item label="Designation" name="designation">
                        <Input placeholder="Enter designation" />
                    </Form.Item>

                    <Form.Item label="Priority" name="priority">
                        <InputNumber style={{ width: '100%' }} min={0} placeholder="e.g. 1" />
                    </Form.Item>

                    {/* Upload and store fileList in media.url */}
                    <Form.Item
                        label="Upload Image"
                        name={['media', 'url']}
                        valuePropName="fileList"
                        getValueFromEvent={normFileList}
                        rules={[
                            {
                                validator: async (_: any, values: any[]) => {
                                    // Optional: require image only when creating
                                    if (!editingId && (!Array.isArray(values) || values.length === 0)) {
                                        return Promise.reject('Please upload image')
                                    }
                                    return Promise.resolve()
                                },
                            },
                        ]}
                    >
                        <S3Upload
                            cdnUrl={YODA_CDN_URL}
                            preSignUrl={YODA_PRESIGN_URL}
                            min={0}
                            max={1}
                            allowedFileExtension={[
                                AllowedFormats.PNG,
                                AllowedFormats.JPEG,
                                AllowedFormats.JPG,
                                AllowedFormats.WEBP,
                            ]}
                            onChange={(info: any) => {
                                const list = Array.isArray(info) ? info : info?.fileList
                                setImageList(list ?? [])
                            }}
                        />
                    </Form.Item>

                    <Form.Item label="Description" name="description" style={{ marginTop: 12 }}>
                        <Input.TextArea rows={3} placeholder="Enter description" />
                    </Form.Item>
                </Form>
            </Drawer>
        </Container>
    )
}

export default ManageTrustees
